import axios from 'axios';
import {API_BASE_URL, AuthUserEnterpriseSummary} from './authApi';

export interface MessagingParticipant {
  id: string;
  name: string;
  email: string;
  profilePicture?: string;
  enterprise: AuthUserEnterpriseSummary | null;
  presenceStatus: 'online' | 'sleep' | 'offline';
}

export interface MessagingAttachment {
  type: 'image' | 'video';
  url: string;
  thumbnailUrl?: string;
  fileSize: number;
  mimeType: string;
  durationSeconds?: number;
  uploadedAt: string;
}

export interface MessagingConversation {
  id: string;
  participants: MessagingParticipant[];
  lastMessageAt: string;
  lastMessagePreview: string;
  unreadCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface MessagingMessage {
  id: string;
  conversationId: string;
  sender: MessagingParticipant;
  recipient: MessagingParticipant;
  content: string;
  attachments: MessagingAttachment[];
  isRead: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface MessagingAttachmentInput {
  uploadSessionId: string;
  parts?: Array<{
    partNumber: number;
    etag: string;
  }>;
  durationSeconds?: number;
}

type ApiErrorResponse = {
  message?: string;
  error?: string | {message?: string};
};

const messagingClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const getErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (axios.isAxiosError(error)) {
    const errorData = error.response?.data as ApiErrorResponse | undefined;
    const nestedError =
      typeof errorData?.error === 'string'
        ? errorData.error
        : errorData?.error?.message;

    return errorData?.message || nestedError || error.message || fallbackMessage;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallbackMessage;
};

const authorizedHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
});

export const messagingApi = {
  async getConversations(token: string, view: 'active' | 'archived' = 'active'): Promise<MessagingConversation[]> {
    try {
      const response = await messagingClient.get<{success: boolean; conversations: MessagingConversation[]}>(
        '/conversations',
        {headers: authorizedHeaders(token), params: {view}}
      );
      return response.data.conversations;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to load conversations'));
    }
  },

  async searchContacts(token: string, query: string): Promise<MessagingParticipant[]> {
    try {
      const response = await messagingClient.get<{success: boolean; contacts: MessagingParticipant[]}>(
        '/contacts/search',
        {
          headers: authorizedHeaders(token),
          params: {q: query},
        }
      );
      return response.data.contacts;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to search contacts'));
    }
  },

  async createConversation(token: string, recipientId: string): Promise<MessagingConversation> {
    try {
      const response = await messagingClient.post<{success: boolean; conversation: MessagingConversation}>(
        '/conversations',
        {recipientId},
        {headers: authorizedHeaders(token)}
      );
      return response.data.conversation;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to start conversation'));
    }
  },

  async getConversationMessages(token: string, conversationId: string): Promise<MessagingMessage[]> {
    try {
      const response = await messagingClient.get<{success: boolean; messages: MessagingMessage[]}>(
        `/conversations/${conversationId}`,
        {headers: authorizedHeaders(token)}
      );
      return response.data.messages;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to load messages'));
    }
  },

  async sendMessage(
    token: string,
    payload: {
      conversationId: string;
      recipientId: string;
      content: string;
      attachments?: MessagingAttachmentInput[];
    }
  ): Promise<MessagingMessage> {
    try {
      const response = await messagingClient.post<{success: boolean; message: MessagingMessage}>(
        '/messages',
        payload,
        {headers: authorizedHeaders(token)}
      );
      return response.data.message;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to send message'));
    }
  },

  async archiveConversation(token: string, conversationId: string): Promise<void> {
    try {
      await messagingClient.put(
        `/conversations/${conversationId}/archive`,
        undefined,
        {headers: authorizedHeaders(token)}
      );
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to archive conversation'));
    }
  },

  async unarchiveConversation(token: string, conversationId: string): Promise<void> {
    try {
      await messagingClient.delete(
        `/conversations/${conversationId}/archive`,
        {headers: authorizedHeaders(token)}
      );
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to restore conversation'));
    }
  },

  async markConversationAsRead(token: string, conversationId: string): Promise<void> {
    try {
      await messagingClient.put(
        `/conversations/${conversationId}/read`,
        undefined,
        {headers: authorizedHeaders(token)}
      );
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to mark this conversation as read'));
    }
  },
};
